import { makeAutoObservable } from 'mobx'

import type { LessonClass } from '../LessonClass'
import type { Student } from '../Student'
import { SchoolGrade } from '../Student'
import type { StudentGroupT } from '../Student/StudentGroups'

type StudentData = {
  type: 'student'
  studentId: Student['id']
  name: Student['name']
}

type ClassData = {
  type: 'lesson-class'
  classId: LessonClass['id']
  name: LessonClass['name']
}

type GradeData = {
  type: 'grade'
  schoolGrade: SchoolGrade
}

export type GroupType = 'student' | 'grade' | 'lesson-class'

type GroupInfoDataType<T extends GroupType> = T extends 'student'
  ? StudentData
  : T extends 'grade'
    ? GradeData
    : ClassData

/* 학년 / 반 / 학생 3가지 타입의 그룹을 추상화 */
export class GroupInfo<GroupT extends GroupType = GroupType> {
  constructor(readonly data: GroupInfoDataType<GroupT>) {
    makeAutoObservable(this)
  }

  get key() {
    switch (this.data.type) {
      case 'student':
        return this.data.studentId
      case 'grade':
        return this.data.schoolGrade.koLabel
      case 'lesson-class':
        return this.data.classId
    }
  }

  get strKey() {
    return this.key ? `${this.key}` : undefined
  }

  get name() {
    switch (this.data.type) {
      case 'student':
        return this.data.name
      case 'grade':
        return this.data.schoolGrade.koLabel
      case 'lesson-class':
        return this.data.name
    }
  }

  static fromStudent(student: Student) {
    return new GroupInfo({
      type: 'student',
      studentId: student.id,
      name: student.name,
    })
  }

  static fromStudentGroup(studentGroup: StudentGroupT) {
    switch (studentGroup.type) {
      case 'grade':
        return new GroupInfo({
          type: 'grade',
          schoolGrade: SchoolGrade.fromKoLabel(studentGroup.id)!,
        })
      case 'lesson-class':
        return new GroupInfo({
          type: 'lesson-class',
          classId: studentGroup.id,
          name: studentGroup.name,
        })
    }
  }

  isEq(groupInfo?: GroupInfo) {
    return this.key === groupInfo?.key
  }
}
